import React, { useState, useEffect } from 'react';
import { PlayCircle, Trash2, Plus, X, Loader2, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const LanguageClassesManager = ({ role }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        media_url: '',
        media_type: 'video'
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchClasses();
    }, [role]);

    const fetchClasses = async () => {
        try {
            setLoading(true);
            const endpoint = role === 'student' ? '/student/language-classes' : `/${role}/language-classes`;

            const response = await api.get(endpoint);
            setClasses(response.data || []);
        } catch (error) {
            console.error('Error fetching language classes:', error);
            showToast('api.error.fetchFailed', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.media_url) {
            showToast('Please fill all required fields', 'error');
            return;
        }

        try {
            setSubmitting(true);
            const endpoint = `/${role}/language-classes`;
            await api.post(endpoint, formData);
            showToast('Class added successfully', 'success');
            setShowAddModal(false);
            setFormData({ title: '', description: '', media_url: '', media_type: 'video' });
            fetchClasses();
        } catch (error) {
            console.error('Error creating class:', error);
            showToast('Failed to add class', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this class?')) return;

        try {
            const endpoint = `/${role}/language-classes/${id}`;
            await api.delete(endpoint);
            showToast('Class deleted successfully', 'success');
            fetchClasses();
        } catch (error) {
            console.error('Error deleting class:', error);
            showToast('Failed to delete class', 'error');
        }
    };

    const isManagementRole = role === 'staff' || role === 'admin';

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 flex items-center">
                        <BookOpen className="w-6 h-6 mr-2 text-blue-600" />
                        Language Classes & Recordings
                    </h2>
                    <p className="text-slate-600 mt-1">Access or manage recorded language modules.</p>
                </div>
                {isManagementRole && (
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-slate-800 transition-colors"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Class
                    </button>
                )}
            </div>

            {classes.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                    <PlayCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900">No classes available</h3>
                    <p className="text-slate-500 mt-1">Check back later for new modules.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {classes.map((cls) => (
                        <div key={cls.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                            <div className="aspect-video bg-slate-100 flex items-center justify-center relative group">
                                {cls.media_type === 'video' ? (
                                    <iframe
                                        src={cls.media_url}
                                        className="w-full h-full"
                                        allowFullScreen
                                        title={cls.title}
                                    ></iframe>
                                ) : (
                                    <audio controls src={cls.media_url} className="w-[90%]"></audio>
                                )}
                                {isManagementRole && (
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleDelete(cls.id)}
                                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors shadow-sm"
                                            title="Delete class"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="p-5 flex-1 flex flex-col">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full">
                                        {cls.media_type === 'video' ? 'Video' : 'Audio'}
                                    </span>
                                    <span className="text-xs text-slate-500">
                                        {new Date(cls.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-2">{cls.title}</h3>
                                <p className="text-slate-600 text-sm flex-1">{cls.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Modal */}
            {showAddModal && isManagementRole && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-lg text-slate-900">Add Language Class</h3>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleCreate} className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.title}
                                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g., German B1 - Module 1"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                                    <textarea
                                        rows={3}
                                        value={formData.description}
                                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                        placeholder="Provide some details about the content..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Media Type</label>
                                    <select
                                        value={formData.media_type}
                                        onChange={e => setFormData({ ...formData, media_type: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="video">Video (YouTube embed, MP4 URL, etc.)</option>
                                        <option value="audio">Audio (MP3 URL)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Media URL *</label>
                                    <input
                                        type="url"
                                        required
                                        value={formData.media_url}
                                        onChange={e => setFormData({ ...formData, media_url: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="https://..."
                                    />
                                    <p className="text-xs text-slate-500 mt-1">If using YouTube, please use embed links (e.g., https://www.youtube.com/embed/XXXXX)</p>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 bg-black text-white hover:bg-slate-800 rounded-lg transition-colors font-medium flex items-center"
                                >
                                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Save Class
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LanguageClassesManager;
